import React, { ChangeEvent, FC, useEffect, useState } from 'react';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import moment from 'moment';

import { useTimesheetEdit, useTimesheetSelection } from 'src/features/timesheets/providers';
import { DeleteIcon, Modal, WarningFilledIcon } from 'src/shared/components';
import { DAY_DATE_MONTH_FORMAT } from 'src/shared/constants';
import { useTranslate } from 'src/shared/providers';

const NOTE_MAX_LENGTH = 512;

interface Props {
  open: boolean;
  onClose: () => void;
}

export const TimesheetNoteModal: FC<Props> = ({ open, onClose }) => {
  const { t } = useTranslate();

  const { selected } = useTimesheetSelection();
  const { editingTimesheet, onNoteChange } = useTimesheetEdit();

  const [note, setNote] = useState<string>();

  const noteMaxLength = (note?.length ?? 0) >= NOTE_MAX_LENGTH;

  const handleNoteChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    if (value.length > NOTE_MAX_LENGTH) return;
    setNote(value);
  };

  useEffect(
    () =>
      setNote(
        editingTimesheet?.rows
          .find((row) => row.id === selected?.id)
          ?.cells.find((cell) => cell.date === selected?.field)?.note ?? '',
      ),
    [editingTimesheet, selected],
  );

  const handleSave = () => {
    if (!selected || note === undefined) {
      return;
    }

    const isBlank = !note.trim();

    onNoteChange(selected.id, selected.field, isBlank ? null : note);
    onClose();
  };

  if (!selected || note === undefined) {
    return null;
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      modalHeaderProps={{
        title: t('global.addANote'),
        message: moment(selected.field).format(DAY_DATE_MONTH_FORMAT),
        onClose: onClose,
      }}
      modalFooterProps={{
        submitText: t('global.ok'),
        onSubmit: handleSave,
        closeText: t('global.close'),
        onClose,
      }}
    >
      <Stack gap={0.5}>
        <Stack direction="row" gap={1} alignItems="flex-start">
          <TextField
            value={note}
            onChange={handleNoteChange}
            error={noteMaxLength}
            placeholder={t('global.haveSomethingToSayAddItHere')}
            multiline
            sx={{ flex: 1 }}
          />
          <IconButton onClick={() => setNote('')} color="error">
            <DeleteIcon size={20} />
          </IconButton>
        </Stack>
        <Typography
          fontSize={12}
          color={(theme) => (noteMaxLength ? theme.palette.error.main : theme.palette.action.active)}
          sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 1 }}
        >
          {noteMaxLength ? (
            <>
              <WarningFilledIcon size={16} />
              {t('global.noteHasReachedCharacterLimit')}
            </>
          ) : (
            `${note.length}/${NOTE_MAX_LENGTH}`
          )}
        </Typography>
      </Stack>
    </Modal>
  );
};
